import { useCallback, useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import _ from 'lodash';
import cn from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowDownArrowUp, faChevronDown, faArrowDownToLine } from '@fortawesome/pro-regular-svg-icons';

import { getUrlParam, setUrlParam, removeUrlParam } from '../../Helpers/helpers';
import { useApiWithCancel } from '../../CustomHooks/useApiWithCancel';
import { useViewTranstion } from '../../CustomHooks/useViewTransition';
import useElementInView from '../../CustomHooks/useElementInView';
import { downloadCsvFromUrl } from '../../Helpers/helpers';
import { getBrandMonthlyStatPromoters } from '../../APIClient/brands';
import { getBrandId } from '../../Helpers/user_helpers';

import './MonthlyBrandAnalyticsResult.scss';
import MonthlyBrandAnalyticsPromoter, { formatStatValue, rankMonths } from './MonthlyBrandAnalyticsPromoter';
import Loader from '../Loader/Loader';

const MonthlyBrandAnalyticsResult = props => {
  const { user, months, statRow, filters } = props;
  const brandId = getBrandId(user);
  const { stat, data: statData } = statRow;
  const monthRanks = rankMonths(statData, months);
  const userFilter = filters.userFilter.value;
  const dtcOnly = filters.dtcFilter.value;
  const rowHasData = months.some(month => Boolean(statData[month.value]));
  const latestMonthWithData = _.findLast(months, month => Boolean(statData[month.value]), months.length - 2) ?? months.at(-2);

  const viewTransition = useViewTranstion();
  const [fetchPromoters, cancelFetchPromoters] = useApiWithCancel(getBrandMonthlyStatPromoters, false);

  // Filters state
  const [sortMonth, setSortMonth] = useState(() => {
    const urlParam = getUrlParam(stat.value);
    const [month] = urlParam?.split('_') ?? [];
    return month && months.some(m => m.value === month) ? month : latestMonthWithData.value;
  });
  const [sortDirection, setSortDirection] = useState(() => {
    const urlParam = getUrlParam(stat.value);
    const [, order] = urlParam?.split('_') ?? [];
    return order && ['asc', 'decs'].includes(order) ? order : 'desc';
  });
  const [promotersPage, setPromotersPage] = useState(0);

  // Promoters state
  const [promoters, setPromoters] = useState([]);
  const [showPromoters, setShowPromoters] = useState(() => {
    const urlParam = getUrlParam(stat.value);
    const [month] = urlParam?.split('_') ?? [];
    return month && months.some(m => m.value === month);
  });
  const [isLoadingPromoters, setIsLoadingPromoters] = useState(false);
  const [isLoadingMorePromoters, setIsLoadingMorePromoters] = useState(false);
  const [isDownloadingPromoters, setIsDownloadingPromoters] = useState(false);
  const [hasMorePromoters, setHasMorePromoters] = useState(false);
  const drawerEleRef = useRef(null);
  const [loadMoreEleRef, loadMoreTriggered] = useElementInView({
    rootMargin: '0px 0px 250px 0px',
    enabled: showPromoters && promoters?.length && hasMorePromoters
  });

  const loadPromoters = useCallback(async ({ userFilter, dtcOnly, month, direction, page, append, download }) => {
    if (append) setIsLoadingMorePromoters(true);
    if (download) setIsDownloadingPromoters(true);
    else setIsLoadingPromoters(true);

    let params = { sortOrder: stat.value, sortMonth: month, sortDirection: direction, page };
    if (_.isObject(userFilter)) {
      if (userFilter?.BrandList_id) params.BrandList_id = userFilter.BrandList_id;
      if (userFilter?.DiscoverList_id) params.DiscoverList_id = userFilter.DiscoverList_id;
      if (userFilter?.Lookbook_id) params.Lookbook_id = userFilter.Lookbook_id;
      if (userFilter?.Opportunity_id) params.Opportunity_id = userFilter.Opportunity_id;
    } else {
      params.filter = userFilter;
    }
    if (!_.isNil(dtcOnly)) params.dtcOnly = dtcOnly;
    if (download) params.download = 1;

    try {
      const res = await fetchPromoters(brandId, params);
      if (!res) return console.warn('empty stats api response'); // request was cancelled, ignore since there should be another request in progress
      if (download) {
        if (!res.download_url) throw new Error('missing download_url');
        downloadCsvFromUrl(res.download_url);
      } else {
        if (!res.success) throw new Error('promoters apiresponse.success is false', res);
        if (append) setPromoters(prev => prev.concat(res.promoters));
        else setPromoters(res.promoters);
        setHasMorePromoters(res.hasMore);
      }
    } catch (e) {
      console.error('failed to load promoters', e);
      window.ALERT.error(`Failed to ${download ? 'download' : 'load'} promoter stats`);
    }

    setIsLoadingPromoters(false);
    setIsLoadingMorePromoters(false);
    setIsDownloadingPromoters(false);
  });

  const togglePromoters = month => {
    if (showPromoters) {
      removeUrlParam(stat.value);
      return viewTransition(() => setShowPromoters(false));
    }
    if (!promoters?.length || (month && month !== sortMonth)) {
      setPromoters([]);
      cancelFetchPromoters();
      loadPromoters({ userFilter, dtcOnly, month: month ?? latestMonthWithData.value, direction: sortDirection, page: 0 });
    }
    viewTransition(() => setShowPromoters(true));
    setUrlParam(stat.value, `${sortMonth}_${sortDirection}`);
    if (month && month !== sortMonth) {
      setSortMonth(month);
      setSortDirection('desc');
    }
  };

  const toggleCell = month => {
    drawerEleRef.current?.scrollTo(0, 0);
    if (!showPromoters) togglePromoters(month);
    else if (sortMonth === month) {
      const newSortDirection = sortDirection === 'desc' ? 'asc' : 'desc';
      cancelFetchPromoters();
      loadPromoters({ userFilter, dtcOnly, month, direction: newSortDirection, page: 0 });
      setSortDirection(newSortDirection);
      setUrlParam(stat.value, `${month}_${newSortDirection}`);
    } else {
      cancelFetchPromoters();
      loadPromoters({ userFilter, dtcOnly, month, direction: 'desc', page: 0 });
      setSortMonth(month);
      setSortDirection('desc');
      setUrlParam(stat.value, `${month}_desc`);
    }
    setPromotersPage(0);
  };

  // Re-fetch promoters when filters change
  useEffect(() => {
    if (!showPromoters) return;
    drawerEleRef.current?.scrollTo(0, 0);
    loadPromoters({ userFilter, dtcOnly, month: sortMonth, direction: sortDirection, page: 0 });
    setPromotersPage(0);
  }, [userFilter, dtcOnly]);

  // Load more promoters at end of list
  useEffect(() => {
    if (!loadMoreTriggered || !showPromoters || isLoadingPromoters || !hasMorePromoters) return;
    const newPage = promotersPage + 1;
    loadPromoters({ userFilter, dtcOnly, month: sortMonth, direction: sortDirection, page: newPage, append: true });
    setPromotersPage(newPage);
  }, [loadMoreTriggered]);

  return (
    <>
      <div
        className={cn('monthly-brand-stats-table-row', showPromoters && 'monthly-brand-stats-table-row--open')}
        style={{ viewTransitionName: `table-row-${stat.value}` }}
        data-amp-track-row={stat.value}
      >
        {/* Stat row */}
        <div className='monthly-brand-stats-table-row-name'>
          <button disabled={!rowHasData} onClick={() => togglePromoters()} data-amp-track-cell='label'>
            {stat.label}
          </button>
          <button
            onClick={() => loadPromoters({ userFilter, dtcOnly, month: sortMonth, direction: sortDirection, download: true })}
            disabled={isLoadingPromoters || isDownloadingPromoters}
            data-amp-track-cell='download'
          >
            {isDownloadingPromoters ? <Loader size={24} /> : <FontAwesomeIcon icon={faArrowDownToLine} />}
          </button>
        </div>
        {months.map(({ value: monthKey }, i) => (
          <button
            key={stat.value + monthKey}
            className={cn(
              'monthly-brand-stats-table-row-cell',
              i === months.length - 1 && 'monthly-brand-stats-table-row-cell--mtd',
              monthKey in monthRanks && `monthly-brand-stats-table-row-cell--rank-${monthRanks[monthKey]}`,
              sortMonth === monthKey && 'monthly-brand-stats-table-row-cell--sorted'
            )}
            disabled={!statData[monthKey]}
            onClick={() => toggleCell(monthKey)}
            data-amp-track-cell={'col-' + i}
          >
            {formatStatValue(stat.type, statData[monthKey])}
            {showPromoters && (
              <FontAwesomeIcon
                icon={faArrowDownArrowUp}
                className={cn(
                  'monthly-brand-stats-table-row-cell-sort',
                  sortMonth === monthKey && `monthly-brand-stats-table-row-cell-sort--${sortDirection}`
                )}
              />
            )}
          </button>
        ))}
        <button disabled={!rowHasData} onClick={() => togglePromoters()} data-amp-track-cell='icon'>
          <FontAwesomeIcon icon={faChevronDown} />
        </button>
      </div>

      {/* Promoters drawer */}
      <div className='monthly-brand-stats-table-row-drawer' style={{ viewTransitionName: `table-row-promoters-${stat.value}` }}>
        <ul ref={drawerEleRef}>
          {showPromoters &&
            promoters.length > 0 &&
            promoters.map((promoter, i) => (
              <MonthlyBrandAnalyticsPromoter
                key={`${stat.value}-${promoter.User_id}`}
                months={months}
                stat={stat}
                promoter={promoter}
                rank={sortDirection === 'desc' ? i + 1 : undefined}
              />
            ))}

          {/* Load more placeholder rows */}
          {showPromoters &&
            promoters.length > 0 &&
            hasMorePromoters &&
            _.range(3).map(rowNum => (
              <li
                key={`promoter-loader-has-more-${stat.value}-${rowNum}`}
                ref={rowNum === 0 ? loadMoreEleRef : null}
                className='monthly-brand-stats-table-row monthly-brand-stats-table-row-promoter'
              >
                {_.range(8).map(colNum => (
                  <div key={`promoter-loader-has-more-${stat.value}-${rowNum}-${colNum}`} className='monthly-brand-stats-table-loader'>
                    <div />
                  </div>
                ))}
              </li>
            ))}

          {/* Initial load placeholder rows */}
          {showPromoters &&
            isLoadingPromoters &&
            promoters.length === 0 &&
            _.range(11).map(rowNum => (
              <li key={`promoter-loader-${stat.value}-${rowNum}`} className='monthly-brand-stats-table-row monthly-brand-stats-table-row-promoter'>
                {_.range(8).map(colNum => (
                  <div key={`promoter-loader-${stat.value}-${rowNum}-${colNum}`} className='monthly-brand-stats-table-loader'>
                    <div />
                  </div>
                ))}
              </li>
            ))}
        </ul>

        {/* Loading overlay */}
        <div
          className={cn(
            'monthly-brand-stats-table-overlay',
            showPromoters && promoters.length > 0 && isLoadingPromoters && !isLoadingMorePromoters && 'monthly-brand-stats-table-overlay--loading'
          )}
          style={{ viewTransitionName: 'promoter-loader-' + stat.value }}
        />
      </div>
    </>
  );
};

MonthlyBrandAnalyticsResult.propTypes = {
  user: PropTypes.object.isRequired,
  ui: PropTypes.object.isRequired,
  analytics: PropTypes.object.isRequired,
  months: PropTypes.array.isRequired,
  statRow: PropTypes.object.isRequired,
  filters: PropTypes.object.isRequired
};

const mapStateToProps = state => {
  const { user, ui, analytics } = state;
  return { user, ui, analytics };
};

export default connect(mapStateToProps)(MonthlyBrandAnalyticsResult);
